import { UploadFile } from 'antd';

import { DefectPictureRawOutputDto } from 'generated/apiSchemas';
import { db, DefectReportPicture } from 'technical/db';
import { filesToPicturesBase64 } from 'technical/picture-upload-input';

export const defectReportPictureSyncService = {
  async download(pictures: DefectPictureRawOutputDto[]): Promise<void> {
    await db.defectReportPictures.bulkPut(
      pictures.map((picture) => ({
        ...picture,
        desynchronizedAt: null,
        deletedAt: null,
      })),
    );
  },

  async createBulk(defectReportId: string, files: UploadFile[]): Promise<void> {
    const existingIds = new Set(
      (await db.defectReportPictures.bulkGet(files.map((file) => file.uid))) // return all object found or undefined
        .filter(Boolean) // remove undefined values from the list
        .map((file) => file!.id), // only keep the id attribute
    );

    const newFiles = files.filter((file) => !existingIds.has(file.uid));
    if (newFiles.length === 0) {
      return;
    }

    const pictures = await filesToPicturesBase64(newFiles);

    await db.defectReportPictures.bulkAdd(
      pictures.map((picture) => ({
        id: picture.id,
        defectReportId,
        data: picture.data,
        desynchronizedAt: new Date().toISOString(),
        deletedAt: null,
      })),
    );
  },

  async getUnsyncedBatch(limit: number): Promise<DefectReportPicture[]> {
    return db.defectReportPictures
      .filter(
        (picture) =>
          picture.desynchronizedAt !== null && picture.deletedAt === null,
      )
      .limit(limit)
      .toArray();
  },

  async markAsSynchronized(ids: string[]): Promise<void> {
    await db.defectReportPictures.bulkUpdate(
      ids.map((id) => {
        return {
          key: id,
          changes: {
            desynchronizedAt: null,
          },
        };
      }),
    );
  },

  async deleteByDefect(defectReportId: string): Promise<void> {
    await db.defectReportPictures
      .where('defectReportId')
      .equals(defectReportId)
      .delete();
  },

  async markForDeletion(id: string): Promise<void> {
    const picture = await db.defectReportPictures.get(id);
    if (!picture) {
      return;
    }

    if (picture.desynchronizedAt === null) {
      await db.defectReportPictures.update(id, {
        deletedAt: new Date().toISOString(),
        desynchronizedAt: new Date().toISOString(),
      });
    } else {
      await db.defectReportPictures.delete(id);
    }
  },

  async getUnsyncedDeletedBatch(limit: number): Promise<DefectReportPicture[]> {
    return db.defectReportPictures
      .filter(
        (picture) =>
          picture.desynchronizedAt !== null && picture.deletedAt !== null,
      )
      .limit(limit)
      .toArray();
  },

  async deleteLocalBatch(ids: string[]): Promise<void> {
    await db.defectReportPictures.bulkDelete(ids);
  },

  async reset(): Promise<void> {
    await db.defectReportPictures.clear();
  },
};
